import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, ContactShadows, Html } from '@react-three/drei';
import { ContainerWireframe } from './container-wireframe';
import { ContainerBox } from './container-box';
import type { ContainerType, ContainerItem, ContainerStatus } from '../types/import-tracking.types';
import type { Placement } from '../utils/bin-packing';
import { CONTAINER_STATUS_3D_COLORS } from '../utils/container-colors';

const STATUS_LABELS: Record<ContainerStatus, string> = {
  EMPTY: 'Vacío',
  LOADING: 'Cargando',
  SEALED: 'Sellado',
  IN_TRANSIT: 'En tránsito',
  AT_PORT: 'En Puerto',
  DELIVERED: 'Entregado',
};

interface Container3DViewerProps {
  containerType: ContainerType;
  items: ContainerItem[];
  placements?: Placement[];
  selectedItemId?: string | null;
  onItemClick?: (itemId: string) => void;
  status?: ContainerStatus;
  fillPercent?: number;
}

interface RenderedItem {
  itemId: string;
  x: number;
  y: number;
  z: number;
  rotated: boolean;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  color: string;
  description: string;
}

/**
 * Genera posiciones apiladas para items sin posición guardada.
 * Los apila en el suelo del contenedor, uno al lado del otro a lo largo del eje X.
 */
function stackUnpositionedItems(
  unpositioned: ContainerItem[],
  containerWidthCm: number
): RenderedItem[] {
  const result: RenderedItem[] = [];
  let cursorX = 0;
  let cursorY = 0;
  const cursorZ = 0;
  let rowMaxHeight = 0;
  let layerMaxZ = 0;

  for (const item of unpositioned) {
    // Si no cabe en X, avanzar en Y (profundidad)
    if (cursorX + item.lengthCm > containerWidthCm * 1.5 && cursorX > 0) {
      cursorX = 0;
      cursorY += layerMaxZ;
      layerMaxZ = 0;
    }

    result.push({
      itemId: item.id,
      x: cursorX,
      y: cursorY,
      z: cursorZ,
      rotated: false,
      lengthCm: item.lengthCm,
      widthCm: item.widthCm,
      heightCm: item.heightCm,
      color: item.color,
      description: item.description,
    });

    cursorX += item.lengthCm;
    if (item.widthCm > layerMaxZ) layerMaxZ = item.widthCm;
    if (item.heightCm > rowMaxHeight) rowMaxHeight = item.heightCm;
  }

  return result;
}

function StatusOverlay({
  status,
  fillPercent,
  position,
}: {
  status: ContainerStatus;
  fillPercent: number;
  position: [number, number, number];
}) {
  const color = CONTAINER_STATUS_3D_COLORS[status];

  return (
    <Html position={position} center distanceFactor={8} style={{ pointerEvents: 'none' }}>
      <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap select-none">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span>{STATUS_LABELS[status]}</span>
        <span className="text-white/60">|</span>
        <span>{fillPercent.toFixed(0)}%</span>
      </div>
    </Html>
  );
}

function Scene({
  containerType,
  items,
  placements,
  selectedItemId,
  onItemClick,
  status = 'EMPTY',
  fillPercent = 0,
}: Container3DViewerProps) {
  const centerX = containerType.lengthCm / 100 / 2;
  const centerZ = containerType.widthCm / 100 / 2;
  const containerH = containerType.heightCm / 100;

  let renderedItems: RenderedItem[];

  if (placements) {
    // Modo auto-empacado: usar placements del algoritmo
    renderedItems = placements
      .map((p) => {
        const originalItem = items.find((item) => p.itemId.startsWith(item.id));
        if (!originalItem) return null;
        return {
          itemId: p.itemId,
          x: p.x,
          y: p.y,
          z: p.z,
          rotated: p.rotated,
          lengthCm: p.lengthCm,
          widthCm: p.widthCm,
          heightCm: p.heightCm,
          color: originalItem.color,
          description: originalItem.description,
        };
      })
      .filter((item): item is RenderedItem => item !== null);
  } else {
    // Modo manual: mostrar items con posiciones guardadas + apilar los nuevos sin posición
    const positioned = items
      .filter((item) => item.positionX != null)
      .map((item) => ({
        itemId: item.id,
        x: item.positionX!,
        y: item.positionY!,
        z: item.positionZ!,
        rotated: item.isRotated,
        lengthCm: item.isRotated ? item.widthCm : item.lengthCm,
        widthCm: item.isRotated ? item.lengthCm : item.widthCm,
        heightCm: item.heightCm,
        color: item.color,
        description: item.description,
      }));

    const unpositioned = items.filter((item) => item.positionX == null);
    const stacked = stackUnpositionedItems(unpositioned, containerType.lengthCm);

    renderedItems = [...positioned, ...stacked];
  }

  return (
    <>
      <PerspectiveCamera makeDefault position={[centerX + 5, 5, centerZ + 6]} fov={45} />

      {/* Iluminación mejorada */}
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[10, 12, 5]}
        intensity={0.9}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
      <directionalLight position={[-5, 8, -5]} intensity={0.3} />

      <ContainerWireframe
        lengthCm={containerType.lengthCm}
        widthCm={containerType.widthCm}
        heightCm={containerType.heightCm}
        status={status}
      />

      {renderedItems.map((item) => (
        <ContainerBox
          key={item.itemId}
          positionX={item.x}
          positionY={item.y}
          positionZ={item.z}
          lengthCm={item.lengthCm}
          widthCm={item.widthCm}
          heightCm={item.heightCm}
          color={item.color || '#3B82F6'}
          isSelected={selectedItemId === item.itemId}
          onClick={() => onItemClick?.(item.itemId)}
          label={item.description}
        />
      ))}

      {/* Sombras de contacto suaves en el piso */}
      <ContactShadows
        position={[centerX, -0.02, centerZ]}
        opacity={0.4}
        scale={20}
        blur={2}
        far={10}
      />

      {/* Overlay de status flotante */}
      <StatusOverlay
        status={status}
        fillPercent={fillPercent}
        position={[centerX, containerH + 0.5, centerZ]}
      />

      <Grid
        args={[50, 50]}
        position={[centerX, -0.03, centerZ]}
        cellSize={1}
        cellThickness={0.5}
        cellColor="#e2e8f0"
        sectionSize={5}
        sectionThickness={1}
        sectionColor="#94a3b8"
        fadeDistance={30}
        infiniteGrid={false}
      />

      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        target={[centerX, containerH / 2, centerZ]}
      />
    </>
  );
}

export function Container3DViewer(props: Container3DViewerProps) {
  return (
    <div className="w-full bg-muted rounded-lg border" style={{ height: '100%', minHeight: 400 }}>
      <Canvas shadows style={{ width: '100%', height: '100%' }}>
        <Suspense fallback={null}>
          <Scene {...props} />
        </Suspense>
      </Canvas>
    </div>
  );
}
